import React, {CSSProperties, FC, PropsWithChildren, SyntheticEvent, useCallback, useEffect, useState} from 'react';
import './ScrollWithIndicator.less';
import type {uuid} from "../../@types/entity";

const ScrollWithIndicator: FC<ScrollWithIndicatorProps> = props => {
    // The height of the __content box (shown)
    const [clientHeight, setClientHeight] = useState<number>(0);

    // The height of the __content scrollable area
    const [scrollHeight, setScrollHeight] = useState<number>(0);

    // Position of the __content scrollbar
    const [scrollTop, setScrollTop] = useState<number>(0);

    /**
     * Ref for the content area, used during resize to get height and widths, and determine mobile or desktop view.
     */
    const contentRef = React.createRef<HTMLDivElement>();

    /**
     * Resize event. Check the height and width and set the mobile or desktop view.  This can determine which columns are shown.
     *
     * We now care about the height so that we can do virtualized row rendering.
     */
    const onResize = useCallback(() => {
        const element = contentRef.current;

        if (element) {
            if (clientHeight !== element.clientHeight) setClientHeight(element.clientHeight);
            if (scrollHeight !== element.scrollHeight) setScrollHeight(element.scrollHeight);
            if (scrollTop !== element.scrollTop) setScrollTop(element.scrollTop);
        }
    }, [clientHeight, contentRef, scrollHeight, scrollTop]);

    useEffect(() => {
        onResize();
    }, [onResize]);

    /**
     * Called when this element is scrolled
     *
     * @param e
     */
    const onScroll = (e: SyntheticEvent) => {
        e.stopPropagation();

        onResize();
    };

    const className = (props.className || '').concat(' scroll-with-indicator').trim();

    return (
        <div className={className}>
            <div className='scroll-with-indicator__inner'
                 ref={contentRef}
                 onScroll={onScroll}
                 style={props.style}
            >
                {props.children}
            </div>
        </div>
    );
}

export type ScrollWithIndicatorProps = PropsWithChildren<{
    // Optional class name
    className?: string,

    // Optional styles
    style?: CSSProperties,

    // Used for save/restore
    parentId?: uuid,
}>;

export default ScrollWithIndicator;
